import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import type { Message } from './ConversationContext';
import { useTheaterMode } from './TheaterModeContext';        // plan-triad B1: exit restores theater
import { useCameraDirector } from './CameraDirectorContext';  // plan-triad B1: exit clears the ring
// Provider order (App.tsx): Theater ⊃ CameraDirector ⊃ Replay — both are ancestors, no cycle
// (CameraDirectorContext/TheaterModeContext do NOT import ReplayContext).

export type ReplayStep = { anchor: string; endIndex: number };
const BASE_STEP_MS = 1200;   // 1× auto-advance; divided by speed
// plan-triad B2: interval floor MUST exceed useCameraDirector's QUIESCENCE_MS (500ms) or the
// director's fit timer is re-armed every step and never fires — camera/ring freeze at 4×.
const MIN_STEP_MS = 550;

type ReplayValue = {
  active: boolean; stepIndex: number; playing: boolean; speed: 1 | 2 | 4;
  snapshot: ReadonlyArray<Message>; steps: ReadonlyArray<ReplayStep>; priorTheater: boolean;
  enter: (messages: Message[], steps: ReplayStep[], priorTheater: boolean) => void;
  exit: () => void;
  setStepIndex: (k: number) => void;
  togglePlay: () => void;
  setSpeed: (s: 1 | 2 | 4) => void;
};

export const ReplayContext = createContext<ReplayValue>({
  active: false, stepIndex: 0, playing: false, speed: 1, snapshot: [], steps: [], priorTheater: false,
  enter: () => {}, exit: () => {}, setStepIndex: () => {}, togglePlay: () => {}, setSpeed: () => {},
});

export const ReplayProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [active, setActive] = useState(false);
  const [stepIndex, setStepIndexState] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState<1 | 2 | 4>(1);
  const [snapshot, setSnapshot] = useState<ReadonlyArray<Message>>([]);
  const [steps, setSteps] = useState<ReadonlyArray<ReplayStep>>([]);
  const [priorTheater, setPriorTheater] = useState(false);   // plan-triad B1: captured by the banner BEFORE it flips theater
  const { setTheater } = useTheaterMode();
  const { setSpotlightTurnKeys } = useCameraDirector();

  const enter = useCallback((messages: Message[], s: ReplayStep[], pt: boolean) => {
    setSnapshot(messages); setSteps(s); setStepIndexState(0); setPlaying(false); setPriorTheater(pt); setActive(true);
  }, []);
  const exit = useCallback(() => {   // plan-triad B1: single teardown — BOTH exit call sites (banner re-click, transport ✕) route here
    setActive(false); setPlaying(false); setStepIndexState(0); setSnapshot([]); setSteps([]);
    setSpotlightTurnKeys(null);       // clear the amber ring + release the 0.3 dim on the live DAG
    setTheater(priorTheater);         // restore theater to the pre-replay value (NOT a canvas effect that reads it too late)
  }, [priorTheater, setSpotlightTurnKeys, setTheater]);
  const setStepIndex = useCallback((k: number) => {
    setStepIndexState((_) => Math.max(0, Math.min(k, Math.max(0, steps.length - 1))));
  }, [steps.length]);
  const togglePlay = useCallback(() => setPlaying((p) => !p), []);

  useEffect(() => {   // auto-advance while playing; stop at the last step (no loop)
    if (!active || !playing) return;
    if (stepIndex >= steps.length - 1) { setPlaying(false); return; }
    const t = window.setTimeout(() => setStepIndexState((i) => Math.min(i + 1, steps.length - 1)),
      Math.max(MIN_STEP_MS, BASE_STEP_MS / speed));   // plan-triad B2: floor ≥ 550ms
    return () => window.clearTimeout(t);
  }, [active, playing, stepIndex, steps.length, speed]);

  const value = useMemo<ReplayValue>(() => ({
    active, stepIndex, playing, speed, snapshot, steps, priorTheater,
    enter, exit, setStepIndex, togglePlay, setSpeed,
  }), [active, stepIndex, playing, speed, snapshot, steps, priorTheater, enter, exit, setStepIndex, togglePlay]);

  return <ReplayContext.Provider value={value}>{children}</ReplayContext.Provider>;
};

export const useReplay = () => useContext(ReplayContext);
